<template>
  <sp-card v-if="hasContent" class="availability-today-card" variant="flat">
    <sp-card-content>
      <sp-heading v-if="hasTitle" v-preserve-properties:level="4" class="availability-today-card__title">{{
        title
      }}</sp-heading>
      <sp-text v-if="hasText" type="body-semi-bold" tag="p" class="availability-today-card__text">{{ text }}</sp-text>
      <sp-button
        v-if="showLink"
        :disabled="disabled"
        block
        tag="a"
        color="custom"
        density="compact"
        class="availability-today-card__button"
        :href="href"
        :data-testid="TestId.finder.availabilityTodayLink"
        >{{ linkLabel }}</sp-button
      >
    </sp-card-content>
  </sp-card>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { computed } from "vue";
import { TestId } from "#/test-ids";
import { optionalText } from "#ce/shared/filters/availability/i18n";

const props = defineProps({
  disabled: { type: Boolean, default: false },
  /** The per-domain call-to-action link. */
  href: {
    type: String,
    default: undefined,
  },
});

// A client supplies the copy through its `frontend.availability_filter` overrides; core ships it
// blank, so the card renders nothing until a client fills it in.
const title = computed(() => optionalText("today_title"));
const text = computed(() => optionalText("today_text"));
const linkLabel = computed(() => optionalText("today_link_label"));

const hasTitle = computed(() => Boolean(title.value));
const hasText = computed(() => Boolean(text.value));
const showLink = computed(() => Boolean(linkLabel.value && props.href));

const hasContent = computed(() => hasTitle.value || hasText.value || showLink.value);
</script>

<style lang="scss" scoped>
.availability-today-card {
  --padding: var(--sp-comp-availability-filter-today-padding, var(--sp-ref-scale-16));
  --sp-comp-card-padding-block: var(--padding) calc(var(--padding) * 1.5);
  --sp-comp-card-padding-inline: var(--padding);
  --today-button-border-radius: var(
    --sp-comp-availability-filter-today-button-border-radius,
    var(--sp-comp-button-border-radius, 0)
  );

  display: block;
  background-color: var(--sp-comp-availability-filter-today-background-color, var(--sp-sys-color-surface));
  background-image: var(--sp-comp-availability-filter-today-background-image, none);
  background-position: var(--sp-comp-availability-filter-today-background-position, center);
  background-repeat: no-repeat;
  background-size: var(--sp-comp-availability-filter-today-background-size, cover);
  // Unthemed, the card is on-surface text on the surface. A theme that supplies a background image
  // sets the colour to read on it through `--sp-comp-availability-filter-today-color`.
  color: var(--sp-comp-availability-filter-today-color, var(--sp-sys-color-on-surface));
  // The heading and text carry their own typescale; the card's colour reaches them through the text token.
  --sp-comp-text-color: var(--sp-comp-availability-filter-today-color, var(--sp-sys-color-on-surface));

  &__title {
    margin: 0;
  }

  &__text {
    margin: var(--sp-ref-scale-8) 0 0;
  }

  &__button {
    --sp-comp-button-hover-bg-opacity: 1;
    --sp-comp-button-border-radius: var(--today-button-border-radius);
    --sp-comp-button-custom-bg-color: var(
      --sp-comp-availability-filter-today-button-bg-color,
      var(--sp-comp-availability-filter-available-day-content-button-bg-color, var(--sp-sys-color-warning))
    );
    --sp-comp-button-custom-hover-bg-color: var(
      --sp-comp-availability-filter-today-button-hover-bg-color,
      var(--sp-comp-availability-filter-available-day-content-button-hover-bg-color, var(--sp-sys-color-warning))
    );
    --sp-comp-button-custom-text-color: var(
      --sp-comp-availability-filter-today-button-text-color,
      var(--sp-comp-availability-filter-available-day-content-button-text-color, var(--sp-sys-color-on-warning))
    );
    --sp-comp-button-custom-hover-text-color: var(
      --sp-comp-availability-filter-today-button-hover-text-color,
      var(--sp-comp-availability-filter-available-day-content-button-hover-text-color, var(--sp-sys-color-on-warning))
    );

    margin-top: var(--sp-ref-scale-16);
  }
}
</style>
